// Create-engagement mutation.
//
// Runs two backend calls:
// 1. POST /engagements/        → creates the engagement row
// 2. POST /engagements/:id/assets/bulk → attaches parsed targets as assets
//
// On success, invalidates both the engagements list and dashboard queries
// so they refetch with the new data.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Engagement } from "@/types/engagement";

export interface CreateEngagementInput {
  name: string;
  client_name: string;
  pentest_type: string;
  scope_description: string;
  targets: string[]; // one per line, caller passes the split array
}

type AssetType = "url" | "ip" | "domain";

interface AssetInput {
  asset_type: AssetType;
  value: string;
}

function parseTarget(target: string): AssetInput {
  if (target.startsWith("http://") || target.startsWith("https://")) {
    return { asset_type: "url", value: target };
  }
  if (/^\d+\.\d+\.\d+\.\d+/.test(target)) {
    return { asset_type: "ip", value: target };
  }
  return { asset_type: "domain", value: target };
}

export function useCreateEngagement() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateEngagementInput): Promise<Engagement> => {
      const engagement = await api<Engagement>("/engagements/", {
        method: "POST",
        body: {
          name: input.name,
          client_name: input.client_name || null,
          pentest_type: input.pentest_type,
          scope_description: input.scope_description || null,
        },
      });

      const assets = input.targets.map(parseTarget);
      if (assets.length > 0) {
        await api(`/engagements/${engagement.id}/assets/bulk`, {
          method: "POST",
          body: assets,
        });
      }

      return engagement;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagements"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
